import { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent, IonSpinner, IonToolbar, IonButton, IonButtons, IonIcon, IonRefresher, IonRefresherContent, RefresherEventDetail } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { refreshOutline } from 'ionicons/icons';
import { CourseList, CourseTable, Widgets } from '../components/progress';
import { BadgesTable, CertificationsTable } from '../components/badges';
import { useIsMobile } from '../hooks/useMediaQuery';
import { progressService } from '../services/progressService';
import { TeamUserSummary, TeamUserCourseSummary } from '../types/teamUserSummary';
import { Badge, Certification, Certificate } from '../types/index';
import { useProfileStore } from '../store/useProfileStore';
import { showDataRefreshed, showDataRefreshFailed } from '../services/toastService';
import { useRefresh } from '../contexts/RefreshContext';
import { useBadgeDetection } from '../hooks/useBadgeDetection';
import { subscribeToBadgeModal } from '../services/badgeModalService';

export function MyProgressPage() {
  const [courseData, setCourseData] = useState<TeamUserCourseSummary[]>([]);
  const [widgetData, setWidgetData] = useState<TeamUserSummary | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const profile = useProfileStore((state) => state.activeProfile);
  const router = useIonRouter();
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  const lastShownBadgeIdRef = useRef<string | null>(null);

  // Fetch badges and certifications data
  const fetchBadges = useCallback(async () => {
    if (!profile?.id) {
      setBadgesLoading(false);
      return;
    }

    try {
      setBadgesLoading(true);
      // Fetch all badges, certificates, and earned badges in parallel
      const [allBadges, allCertificates, teamUserSummary] = await Promise.all([
        progressService.getAllBadges(),
        progressService.getAllCertificates(),
        progressService.getTeamUserSummary(profile.id)
      ]);

      // Create a map of certificates by associationId for quick lookup
      const certificatesMap = new Map<string, Certificate>();
      allCertificates.forEach((cert) => {
        if (cert.associationId) {
          certificatesMap.set(cert.associationId, cert);
        }
      });

      // Create a map of earned badges by ID for quick lookup
      const earnedBadgesMap = new Map<string, Badge>();
      if (teamUserSummary.badges) {
        teamUserSummary.badges.forEach((badge) => {
          earnedBadgesMap.set(badge.id, badge);
        });
      }

      // Merge all badges with earned badge data
      // If a badge appears in the summary's badges array, it's earned
      const mergedBadges = allBadges.map((badge) => {
        const earnedBadge = earnedBadgesMap.get(badge.id);
        if (earnedBadge) {
          // Badge is earned - use updatedAt or createdAt as earnedDate
          return {
            ...badge,
            earnedDate: earnedBadge.updatedAt || earnedBadge.createdAt || new Date().toISOString()
          };
        }
        // Badge is not earned - no earnedDate
        return badge;
      });

      setBadges(mergedBadges);

      // Map progress data to certifications
      // Simple comparison: coursesCompleted vs coursesTotal (like the widget)
      const mappedCertifications: Certification[] = teamUserSummary.progress.map((certProgress) => {
        // Get certificate details from the certificates map
        const certificate = certificatesMap.get(certProgress.certificationId);
        
        // Simple status calculation based on coursesCompleted vs coursesTotal
        let status: 'active' | 'expired' | 'pending' = 'pending';
        let completedDate: string = '';
        
        // If all courses are completed
        if (certProgress.coursesCompleted >= certProgress.coursesTotal && certProgress.coursesTotal > 0) {
          status = 'active';
          completedDate = certProgress.updatedAt || certProgress.createdAt || new Date().toISOString();
        } else if (certProgress.coursesCompleted > 0) {
          // In progress - has started but not completed
          // CertificationsTable requires completedDate to show "in-progress"
          // Use updatedAt/createdAt if available, otherwise use current date to indicate it's been started
          status = 'pending';
          completedDate = certProgress.updatedAt || certProgress.createdAt || new Date().toISOString();
        } else {
          // Not started - no courses completed
          status = 'pending';
          completedDate = '';
        }

        return {
          id: certProgress.certificationId,
          name: certificate?.name || certProgress.name,
          description: certificate?.description || `Progress: ${certProgress.coursesCompleted}/${certProgress.coursesTotal} courses completed`,
          issuer: 'Rev Leasing', // Default issuer
          completedDate: completedDate,
          credentialId: certProgress.certificationId,
          status: status,
          certificateUrl: certificate?.template // Use template URL if available
        };
      });

      setCertifications(mappedCertifications);
    } catch (err) {
      console.error('Failed to load badges:', err);
      setBadges([]);
      setCertifications([]);
    } finally {
      setBadgesLoading(false);
    }
  }, [profile?.id]);

  // Refetch data when navigating to this page
  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.id) {
        setLoading(false);
        setBadgesLoading(false);
        return;
      }

      try {
        setLoading(true);
        setBadgesLoading(true);
        setError(null);
        // Clear old data to prevent showing stale test data
        setCourseData([]);
        setWidgetData(null);
        setBadges([]);
        setCertifications([]);

        // Fetch course data, widget data, and badges/certifications in parallel
        const [courseDataResult, widgetDataResult] = await Promise.all([
          progressService.getTeamUserCourseSummary(profile.id),
          progressService.getTeamUserSummary(profile.id)
        ]);

        setCourseData(courseDataResult);
        setWidgetData(widgetDataResult);
        
        // Fetch badges and certifications
        await fetchBadges();
      } catch (err) {
        // Error modal presented by API
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profile?.id, router.routeInfo.pathname, fetchBadges]);

  const handleRefresh = useCallback(async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);
      setBadgesLoading(true);
      setError(null);

      const [courseDataResult, widgetDataResult] = await Promise.all([
        progressService.getTeamUserCourseSummary(profile.id),
        progressService.getTeamUserSummary(profile.id)
      ]);

      setCourseData(courseDataResult);
      setWidgetData(widgetDataResult);
      
      // Refresh badges and certifications
      await fetchBadges();
      
      await showDataRefreshed();
    } catch (err) {
      // Error modal presented by API
      setError('Failed to refresh data');
      await showDataRefreshFailed(err instanceof Error ? err.message : undefined);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, fetchBadges]);

  // Detect newly earned badges
  useBadgeDetection(widgetData?.badges);

  // Register refresh handler for Test Panel
  useEffect(() => {
    registerRefreshHandler('/my-progress', handleRefresh);
    return () => {
      unregisterRefreshHandler('/my-progress');
    };
  }, [registerRefreshHandler, unregisterRefreshHandler, handleRefresh]);

  const handleRefreshPull = async (event: CustomEvent<RefresherEventDetail>) => {
    if (!profile?.id) {
      event.detail.complete();
      return;
    }

    try {
      setError(null);

      const [courseDataResult, widgetDataResult] = await Promise.all([
        progressService.getTeamUserCourseSummary(profile.id),
        progressService.getTeamUserSummary(profile.id)
      ]);

      setCourseData(courseDataResult);
      setWidgetData(widgetDataResult);
      
      // Refresh badges and certifications
      await fetchBadges();
      
      await showDataRefreshed();
    } catch (err) {
      // Error modal presented by API
      setError('Failed to refresh data');
      await showDataRefreshFailed(err instanceof Error ? err.message : undefined);
    } finally {
      setLoading(false);
      event.detail.complete();
    }
  };

  // Subscribe to badge modal events to refetch when a badge is shown
  useEffect(() => {
    const unsubscribe = subscribeToBadgeModal((state) => {
      // When a badge modal opens with a new badge, refetch the badges data
      if (state.isOpen && state.currentBadge) {
        const currentBadgeId = state.currentBadge.id;
        // Only refetch if this is a different badge than we last saw
        if (currentBadgeId !== lastShownBadgeIdRef.current) {
          lastShownBadgeIdRef.current = currentBadgeId;
          // Refetch badges data to update the page
          fetchBadges();
        }
      }
    });

    return unsubscribe;
  }, [fetchBadges]);

  return (
    <IonPage>
      <IonContent className="page-content bg-gray-50">
        <IonRefresher slot="fixed" onIonRefresh={handleRefreshPull}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <div className={`page-header ${isMobile ? 'page-header-mobile' : 'page-header-desktop'}`}>
          <div className="title">My Progress</div>
        </div>

        {widgetData && <Widgets data={widgetData} />}

        {/* Certifications and Badges Section */}
        <section className="w-full py-2.5" data-tour="certifications-and-badges">
          <div className="flex flex-col gap-6">
            <div className="w-full">
              {badgesLoading ? (
                <div className="flex justify-center items-center py-8">
                  <IonSpinner name="crescent" />
                  <span className="ml-4">Loading certifications...</span>
                </div>
              ) : (
                <CertificationsTable certifications={certifications} />
              )}
            </div>
            <div className="w-full">
              {badgesLoading ? (
                <div className="flex justify-center items-center py-8">
                  <IonSpinner name="crescent" />
                  <span className="ml-4">Loading badges...</span>
                </div>
              ) : (
                <BadgesTable badges={badges} />
              )}
            </div>
          </div>
        </section>

        {/* Course Progress Section */}
        <section className="w-full py-2.5" data-tour="course-progress-table">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-3 border-b border-gray-200" style={{ backgroundColor: 'rgba(var(--ion-color-secondary-rgb), 0.15)' }}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">
                  Course Progress
                  {courseData && courseData.length > 0 && (
                    <span className="text-base font-normal"> - {courseData.length} {courseData.length === 1 ? 'course' : 'courses'}</span>
                  )}
                  {!loading && courseData.length === 0 && (
                    <span className="text-base font-normal text-[#928881]"> - No Course Data</span>
                  )}
                </h2>
                <IonButton onClick={handleRefresh} color="secondary" fill="clear" className="toolbar-button-icon" shape="round">
                  <IonIcon icon={refreshOutline} slot="icon-only" />
                </IonButton>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-8">
                <IonSpinner name="crescent" />
                <span>Loading course data...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div>
                {error}
              </div>
            )}

            {/* No Course Data */}
            {!loading && courseData.length === 0 && (
              <div className="flex flex-col justify-center items-center py-8 gap-4">
                <p>No Course Progress</p>
                <IonButton color="primary" onClick={() => router.push('/all-courses', 'none')}>
                  Start Your Courses
                </IonButton>
              </div>
            )}

            {/* Data Loaded */}
            {courseData && courseData.length > 0 && !loading && (
              isMobile ? (
                <CourseList
                  courses={courseData}
                />
              ) : (
                <CourseTable
                  courses={courseData}
                />
              )
            )}
          </div>
        </section>
      </IonContent>
    </IonPage>
  );
}
